import { TextTypeEnum, VignetteTextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';

export const DEFAULT_TEXT_PLACEHOLDER = 'Текст';

/** What the canvas asks of a field with no name of its own — free text, or a type this list does not know. */
export const GENERIC_TEXT_INPUT_PROMPT = 'Введите текст';

/**
 * The one word list for a text field's human name: the editor's grey placeholder and every "field is empty"
 * refusal (approve, print) speak the same word, so nothing drifts — «Школа» on the cover is at once the
 * field's name and the whole instruction. Free text is the one field with no name of its own: there, and
 * only there, the canvas asks for the second form (`textPlaceholderFor(type, 'prompt')` → «Введите текст»),
 * because a refusal listing «Введите текст» among its fields would read broken.
 */
export const TEXT_TYPE_PLACEHOLDERS: Record<string, string> = {
  [TextTypeEnum.FREE_TEXT]: 'Текст',
  [TextTypeEnum.SCHOOL]: 'Школа',
  [TextTypeEnum.GRADUATION_YEAR]: 'Год выпуска',
  [TextTypeEnum.CITY]: 'Город',
  [TextTypeEnum.CLASS]: 'Класс',
  [TextTypeEnum.PERSON_FIRST_NAME]: 'Имя',
  [TextTypeEnum.PERSON_LAST_NAME]: 'Фамилия',
  [TextTypeEnum.PERSON_FULL_NAME]: 'Имя Фамилия',
  [TextTypeEnum.PERSON_GIVEN_PATRONYMIC]: 'Имя Отчество',
  [TextTypeEnum.PERSON_PATRONYMIC]: 'Отчество',
  [TextTypeEnum.PERSON_QUOTE]: 'Цитата',
  [TextTypeEnum.PERSON_SUBJECT]: 'Предмет',
  [VignetteTextTypeEnum.TITLE]: 'Заголовок',
  [VignetteTextTypeEnum.SUBTITLE]: 'Подзаголовок',
  [VignetteTextTypeEnum.SUBTITLE2]: 'Подзаголовок 2',
  [VignetteTextTypeEnum.SUBTITLE3]: 'Подзаголовок 3',
  [VignetteTextTypeEnum.CLASS_QUOTE]: 'Цитата класса',
};

/**
 * The demo quote the master album shows in a PERSON_QUOTE frame: it is filler, not data, so it says so in its
 * first sentence and then keeps going — the point is to show how much text the frame swallows at the chosen
 * size and font. The student's own album replaces it with a real quote.
 */
export const QUOTE_DEMO_TEXT =
  'Здесь будет цитата ученика. Это демонстрационное наполнение: в эталонном альбоме оно показывает, ' +
  'сколько текста поместится в рамку при выбранном размере и шрифте. У каждого ученика в его альбоме будет своя цитата.';

/** The opening sentence — what a stored quote is matched against to tell demo filler from a real quote. */
export const QUOTE_DEMO_PREFIX = 'Здесь будет цитата ученика.';

/** Albums saved with an earlier wording of the filler still hold demo text, not the photographer's own words. */
const LEGACY_QUOTE_DEMO_TEXTS = [
  'Здесь будет цитата ученика. Это демонстрационное наполнение: в мастер-альбоме оно показывает, ' +
    'сколько текста поместится в рамку при выбранном размере и шрифте. У каждого ученика в его альбоме будет своя цитата.',
];

/**
 * A cut shorter than this stays the photographer's: «Здесь» is a word a person types, and no frame is filled
 * by three syllables. The whole first sentence is NOT the floor — the frame was also filled with the line
 * cut before its period, and that one is filler too.
 */
const QUOTE_DEMO_MIN_MATCH = 20;

/**
 * Demo filler, not a person's quote. Older master albums filled the frame by repeating the sentence word by
 * word until it spilled, so demo text is whatever reads as a prefix of that endless repetition — one
 * sentence, several of them, the line cut before its period, or a tail cut mid-word. A photographer's own
 * words break the run at their first word, and the text stays his.
 */
export function isQuoteDemoText(text: string | null | undefined): boolean {
  const normalized = text?.trim().replace(/\s+/g, ' ');
  if (!normalized || normalized.length < QUOTE_DEMO_MIN_MATCH) return false;
  return [QUOTE_DEMO_TEXT, ...LEGACY_QUOTE_DEMO_TEXTS].some(demo => {
    const repeated = `${demo} `;
    return repeated.repeat(Math.ceil(normalized.length / repeated.length)).startsWith(normalized);
  });
}

/**
 * Fields whose name does not work as the word inside an empty box. «Цитата» over an empty frame reads as a
 * label; the master needs to see that the quote is simply not set yet. The refusal lists keep the name form.
 */
const TEXT_TYPE_PROMPTS: Record<string, string> = {
  [TextTypeEnum.PERSON_QUOTE]: 'Цитата не задана',
  // A blank SUBTITLE3 is the vignette title's year line waiting for the album year (applyProjectTextsToVignettes).
  [VignetteTextTypeEnum.SUBTITLE3]: TEXT_TYPE_PLACEHOLDERS[TextTypeEnum.GRADUATION_YEAR],
};

/**
 * One list, two forms of the same word. `'name'` (default) names the field in a sentence — «Не заполнено:
 * Текст, Школа» — and every refusal keeps it. `'prompt'` is what stands INSIDE the empty box on canvas: a
 * named field prompts with its own name («Школа» on the cover already says what to type), a field with no
 * name of its own asks «Введите текст» — a line that would read broken inside a refusal list — and the few
 * fields listed in `TEXT_TYPE_PROMPTS` say their own sentence.
 */
export function textPlaceholderFor(textType: string | undefined, form: 'name' | 'prompt' = 'name'): string {
  const prompt = form === 'prompt' && textType ? TEXT_TYPE_PROMPTS[textType] : undefined;
  if (prompt) return prompt;
  const name = (textType ? TEXT_TYPE_PLACEHOLDERS[textType] : undefined) ?? DEFAULT_TEXT_PLACEHOLDER;
  return form === 'prompt' && name === DEFAULT_TEXT_PLACEHOLDER ? GENERIC_TEXT_INPUT_PROMPT : name;
}
